import React from "react";
import { useMemo, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancementTogglesPanel } from "@/components/shared/EnhancementTogglesPanel";
import { SitelinksSection } from "@/components/shared/SitelinksSection";
import { useEnhancementConfig } from "@/hooks/useEnhancementConfig";
import {
  accountSettingsToToggleValues,
  toggleValuesToAccountSettings,
  type AccountEnhancementData,
} from "@/utils/enhancementMappingUtils";

interface SetupCreativeEnhancementsProps {
  data: AccountEnhancementData;
  onChange: (data: Partial<AccountEnhancementData>) => void;
  onBatchChange?: (data: Partial<AccountEnhancementData>) => void;
}

export function SetupCreativeEnhancements({ data, onChange, onBatchChange }: SetupCreativeEnhancementsProps) {
  const { data: config } = useEnhancementConfig();

  const values = useMemo(
    () => config ? accountSettingsToToggleValues(data, config.toggles) : {},
    [data, config],
  );

  const showSitelinks = useMemo(() => {
    return Object.entries(values).some(
      ([key, val]) => (key.includes("site_links") || key.includes("site_extensions")) && val,
    );
  }, [values]);

  const handleChange = useCallback(
    (key: string, value: boolean) => {
      if (!config) return;
      const nextValues = { ...values, [key]: value };
      onChange(toggleValuesToAccountSettings(nextValues, config.toggles));
    },
    [config, values, onChange],
  );

  const handleBatchChange = useCallback(
    (updates: Record<string, boolean>) => {
      if (!config) return;
      const nextValues = { ...values, ...updates };
      const mapped = toggleValuesToAccountSettings(nextValues, config.toggles);
      if (onBatchChange) {
        onBatchChange(mapped);
      } else {
        onChange(mapped);
      }
    },
    [config, values, onChange, onBatchChange],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Creative Enhancements</CardTitle>
          <CardDescription>
            Set default Advantage+ creative enhancements for new ads. All enhancements are OFF by default.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EnhancementTogglesPanel values={values} onChange={handleChange} onBatchChange={handleBatchChange} />
        </CardContent>
      </Card>

      {showSitelinks && (
        <SitelinksSection
          sitelinks={data.sitelinks ?? []}
          onChange={(sitelinks) => onChange({ sitelinks })}
        />
      )}
    </div>
  );
}
